import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { usePublicSession } from "@/lib/context/public-session-context";
import { usePortfolioProductCosts } from "@/lib/hooks/use-portfolio-product-costs";
import type { ProductCostsConfig } from "@/lib/types/flow";
import { useCostOverrideEditor } from "../cost-override";
import { ProductCostsWidget } from "../product-costs-widget";
import { T } from "./portfolio-product-costs.i18n";

type PortfolioProductCostsProps = {
  portfolioId: string | null;
  config: Record<string, unknown>;
  language: string;
  /** QL-20 — the advisor's instance; with it, overridable fees can be edited. */
  instanceId?: string | null;
  /** QL-20 — a locked instance (or read-only viewer) shows overrides but never edits. */
  locked?: boolean;
};

/**
 * QL-417 — product costs of one portfolio, on the portfolio page: the new
 * portfolio by default, or the followup's existing holdings when the designer
 * picked "existing". Both carry a weighted-cost row and a coverage note.
 */
export function PortfolioProductCosts({
  portfolioId,
  config,
  language,
  instanceId = null,
  locked = false,
}: PortfolioProductCostsProps) {
  const t = pickT(T, language);
  const { isPublic } = usePublicSession();
  const editor = useCostOverrideEditor(isPublic ? null : instanceId, { locked });
  const cfg = config as ProductCostsConfig;
  // The server falls back to "new" on a non-followup portfolio and says so in
  // `productCosts.source`, which picks the coverage note wording.
  const { productCosts, isLoading } = usePortfolioProductCosts(
    portfolioId,
    cfg.portfolioSource ?? "new",
    language,
  );

  if (isLoading) return null;

  return (
    <VisualizationContainer config={config}>
      {!productCosts || productCosts.products.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.noProductCostsAvailable}
        </div>
      ) : (
        <ProductCostsWidget
          products={productCosts.products}
          costTypes={productCosts.costTypes}
          weighted={productCosts.weighted}
          coveragePct={productCosts.coveragePct}
          source={productCosts.source}
          // The existing holdings are the client's current fees: never overridden.
          onEditCost={editor.canReset && productCosts.source === "new" ? editor.edit : undefined}
          canOverride={editor.canEdit}
        />
      )}
      {editor.dialog}
    </VisualizationContainer>
  );
}
